import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { TailSpin } from "react-loader-spinner";
import "./styles.scss";
import TopNav from "../../components/TopNav/TopNav";
import SideBar from "../../components/SideBar/SideBar";
import chevronLeft from "../../assets/images/icons/chevron-left.svg";
import VenueInformation from "../../components/VenueInformation/VenueInformation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Skeleton from "../../components/Skeleton/Skeleton";
import Button from "../../components/Buttons/Button";
import VenueFeeFields from "../../components/VenueFeeFields/VenueFeeFields";

export default function AcceptVenueRequest() {
  const { venueRequestId } = useParams();
  const navigate = useNavigate();
  const [venueRequest, setVenueRequest] = useState();
  const [venue, setVenue] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissions, setPermissions] = useState([]); // Initialize as empty array
  const [fee, setFee] = useState({ fee_percent: "", drop_fee_percent: "", billing_email: "" });

  useEffect(() => {
    setIsLoading(true);
    async function loadRequest() {
      try {
        const { data: venueRequest } = await api.get(`/venue-requests/${venueRequestId}`);
        setVenueRequest(venueRequest);
        setVenue(venueRequest.venue);
        // Initialize permissions from venueRequest or venue, if available
        setPermissions(venueRequest?.permissions || []);
      } catch (err) {
        console.error(err);
        toast.error("Error loading venue request. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }
    loadRequest();
  }, [venueRequestId]);

  const handleAcceptRequest = useCallback(async () => {
    try {
      // Only send the fee fields that were filled in; blank keeps the standard rate.
      const feeBody = Object.fromEntries(Object.entries(fee).filter(([, v]) => String(v).trim() !== ""));
      await api.put(`/venue-requests/accept/${venueRequest._id}`, { permissions, ...feeBody });
      toast.success("This request was accepted.");
      setPermissions([]); // Clear permissions after successful update
      navigate("/moderators");
    } catch (err) {
      console.error(err);
      toast.error("An error occurred. Please try again or contact support.");
    }
  }, [venueRequest, permissions, fee, navigate]);

  const handleDenyRequest = useCallback(async () => {
    try {
      await api.put(`/venue-requests/deny/${venueRequest._id}`, {});
      toast.success("This request was rejected.");
      setPermissions([]); // Optionally clear permissions after deny
      navigate("/moderators");
    } catch (err) {
      console.error(err);
      toast.error("An error occurred. Please try again or contact support.");
    }
  }, [venueRequest, navigate]);

  if (!venueRequest) {
    return (
        <main>
          <TopNav />
          <div className="main-container">
            <SideBar />
            <div id="venue-request__container">
              <TailSpin width={30} height={30} color="#fff" />
            </div>
          </div>
        </main>
    );
  }

  return (
      <main>
        <TopNav />
        <div className="main-container">
          <SideBar />
          <article>
            <div className="venue-request">
              <div className="venue-request__top">
                <div className="venue-request__top-left">
                  <img
                      src={chevronLeft}
                      className="venue-request__top-left-icon"
                      onClick={() => navigate(-1)}
                      alt="Back"
                  />
                  <h1 className="venue-request__top-left-heading">Viewing venue request</h1>
                </div>
              </div>
              {isLoading ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}><Skeleton type="row" /><Skeleton type="row" /><Skeleton type="row" /><Skeleton type="row" /></div>
              ) : (
                  <>
                    <VenueInformation
                        specVenue={venue || venueRequest}
                        permissions={permissions}
                        setPermissions={setPermissions}
                        showPermissions={true}
                        view
                    />
                    {venueRequest?.comments && (
                      <div style={{
                        background: '#0a0a0a',
                        border: '1px solid #1a1a1a',
                        borderRadius: 14,
                        padding: 18,
                        margin: '20px 0',
                      }}>
                        <div style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: 'rgba(255,255,255,0.4)',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          marginBottom: 8,
                        }}>
                          Note from requester
                        </div>
                        <p style={{
                          fontSize: 14,
                          color: 'rgba(255,255,255,0.8)',
                          lineHeight: 1.55,
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                        }}>
                          {venueRequest.comments}
                        </p>
                      </div>
                    )}
                    <VenueFeeFields
                        venueId={venueRequest.venue_id || venue?._id}
                        value={fee}
                        onChange={setFee}
                    />
                    <div className="venue-request__button-container">
                      <Button primaryBtn onClick={handleAcceptRequest}>
                        Accept
                      </Button>
                      <Button className="btn-outline-light" onClick={handleDenyRequest}>
                        Deny
                      </Button>
                    </div>
                  </>
              )}
            </div>
          </article>
        </div>
      </main>
  );
}